/**
 * BeBobi delivery step (/pages/dostawa) - loaded only by
 * sections/bebobi-delivery-step.liquid.
 *
 * Kurier InPost  -> clear any locker attributes, go to checkout.
 * Paczkomat InPost -> open the Progus picker (window.InPostPickup, API v1),
 *   verify the locker landed in the cart attributes, go to checkout.
 *
 * The picker stores the point under language-dependent cart attribute names
 * (PL storefront vs. any other); both sets are cleared and both are accepted
 * as "a locker was chosen". Shipy hides the Paczkomat rate in checkout unless
 * `Paczkomat InPost` is present - that is the enforcement, this is the UX.
 *
 * The cart form's hidden attributes[locale] input never submits any more
 * (the checkout button is redirected here), so `locale` is written from
 * here to keep the bilingual transactional mails working.
 */

const PREFIX = '[bb-delivery]';
const log = (...a) => console.debug(PREFIX, ...a);

/* PL storefront + other-language storefront attribute names (Progus docs §7) */
const LOCKER_ATTRS = [
  'Wybór dostawy InPost', 'Paczkomat InPost', 'Adres Paczkomatu InPost', 'Współrzędne Paczkomatu InPost',
  'Pickup delivery choice', 'Pickup point name', 'Pickup point address', 'Pickup point coordinates',
  'Pickup point carrier', 'Pickup point country', 'Pickup point payment available',
];
const SELECTED_ATTRS = ['Paczkomat InPost', 'Pickup point name'];

const root = document.getElementById('bb-delivery');
if (!root) throw new Error(PREFIX + ' root element missing');

const cfg = {
  locale: root.dataset.locale || 'pl',
  rootUrl: root.dataset.root || '/',
  cartUrl: root.dataset.cartUrl || '/cart',
  timeoutMs: parseInt(root.dataset.timeout, 10) || 6000,
  blocked: new Set((root.dataset.blockedSkus || '').split(/[,\s;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)),
};
const T = JSON.parse(root.querySelector('[data-bb-i18n]').textContent);
const checkoutUrl = cfg.rootUrl.replace(/\/?$/, '/') + 'checkout';

const ui = {
  buttons: [...root.querySelectorAll('[data-choice]')],
  paczkomat: root.querySelector('[data-bb-paczkomat]'),
  note: root.querySelector('[data-bb-blocked-note]'),
  status: root.querySelector('[data-bb-status]'),
};

let busy = false;
function lock(on) {
  busy = on;
  for (const b of ui.buttons) b.disabled = on;
  root.classList.toggle('bb-delivery--busy', on);
}
function status(text, kind) {
  ui.status.textContent = text || '';
  ui.status.dataset.kind = kind || '';
  ui.status.hidden = !text;
}
function courierOnly(reason) {
  ui.paczkomat.hidden = true;
  ui.note.hidden = false;
  if (reason) log('paczkomat hidden:', reason);
}

/* ---- cart ------------------------------------------------------------- */
const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
async function getCart() {
  const r = await fetch(cfg.cartUrl + '.js', { headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error('cart.js ' + r.status);
  return r.json();
}
async function updateAttributes(attributes) {
  const r = await fetch(cfg.cartUrl + '/update.js', { method: 'POST', headers: json, body: JSON.stringify({ attributes }) });
  if (!r.ok) throw new Error('cart/update.js ' + r.status);
  return r.json();
}
const hasLocker = (cart) => SELECTED_ATTRS.some((k) => String((cart.attributes || {})[k] || '').trim() !== '');

/** Belt and braces: the app's own clear() when present, then a blank write of every name. */
async function clearLocker() {
  if (window.InPostPickup && typeof window.InPostPickup.clear === 'function') {
    try { await window.InPostPickup.clear(); } catch (err) { log('InPostPickup.clear failed', err); }
  }
  const attributes = { locale: cfg.locale };
  for (const k of LOCKER_ATTRS) attributes[k] = '';
  await updateAttributes(attributes);
  log('locker attributes cleared');
}

/* ---- Progus API ------------------------------------------------------- */
function whenInPostReady(timeoutMs) {
  return new Promise((resolve, reject) => {
    if (window.InPostPickup) { resolve(window.InPostPickup); return; }
    const timer = setTimeout(() => {
      document.removeEventListener('inpost:pickup:ready', onReady);
      reject(new Error('timeout'));
    }, timeoutMs);
    function onReady(evt) {
      clearTimeout(timer);
      resolve((evt.detail && evt.detail.api) || window.InPostPickup);
    }
    document.addEventListener('inpost:pickup:ready', onReady, { once: true });
  });
}

/* ---- choices ---------------------------------------------------------- */
async function chooseKurier() {
  if (busy) return;
  lock(true);
  status('');
  try {
    await clearLocker();
  } catch (err) {
    console.warn(PREFIX, 'clear before checkout failed, continuing', err);
  }
  log('kurier -> checkout');
  window.location.href = checkoutUrl;
}

async function choosePaczkomat() {
  if (busy) return;
  lock(true);
  status(T.loading, 'loading');

  let api;
  try {
    api = await whenInPostReady(cfg.timeoutMs);
  } catch (_) {
    log('InPostPickup not available within', cfg.timeoutMs, 'ms');
    status(T.unavailable, 'error');
    lock(false);
    return;
  }

  try {
    const avail = await api.isAvailable();
    if (!avail || avail.available === false) {
      courierOnly('isAvailable: ' + (avail && avail.reason));
      status('');
      lock(false);
      return;
    }
  } catch (err) {
    log('isAvailable threw, trying open() anyway', err);
  }

  let point;
  try {
    point = await api.open({ carrier: 'inpost', locale: cfg.locale });
  } catch (err) {
    courierOnly('open rejected: ' + (err && err.reason || err && err.code || err));
    status('');
    lock(false);
    return;
  }

  if (!point) {                                   // closed without choosing
    log('picker closed without a choice');
    status('');
    lock(false);
    return;
  }

  log('point chosen', point.name, point.address);
  try {
    await updateAttributes({ locale: cfg.locale });
    const cart = await getCart();
    if (!hasLocker(cart)) {
      log('point not found in cart attributes', cart.attributes);
      status(T.notSaved, 'error');
      lock(false);
      return;
    }
  } catch (err) {
    console.warn(PREFIX, 'verification failed', err);
    status(T.notSaved, 'error');
    lock(false);
    return;
  }
  log('paczkomat -> checkout');
  window.location.href = checkoutUrl;
}

/* ---- boot ------------------------------------------------------------- */
(async function boot() {
  let cart;
  try {
    cart = await getCart();
  } catch (err) {
    console.warn(PREFIX, 'cart unreadable, back to cart', err);
    window.location.replace(cfg.cartUrl);
    return;
  }
  if (!cart.item_count || !(cart.items || []).length) {
    log('empty cart -> /cart');
    window.location.replace(cfg.cartUrl);
    return;
  }

  const skus = (cart.items || []).map((i) => String(i.sku || '').trim().toLowerCase());
  const blockedHit = skus.filter((s) => cfg.blocked.has(s));
  const blocked = blockedHit.length > 0;
  if (blocked) courierOnly('blocked SKU in cart: ' + blockedHit.join(', '));
  else { ui.paczkomat.hidden = false; ui.note.hidden = true; }

  if (blocked && hasLocker(cart)) {
    log('stale locker attribute with a blocked SKU, clearing');
    try { await clearLocker(); } catch (err) { console.warn(PREFIX, 'stale clear failed', err); }
  }

  for (const b of ui.buttons) {
    b.addEventListener('click', (evt) => {
      evt.preventDefault();
      if (b.dataset.choice === 'kurier') chooseKurier();
      else choosePaczkomat();
    });
  }
  root.hidden = false;
  lock(false);
  log('ready', { locale: cfg.locale, items: skus.length, blocked });
})();
